import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { MonthlyProductType } from '../../../license/types/MonthlyProductType';
import { LicenseProductStatus, LicenseUsage } from '../licensingGrid/types/licensing-grid-license';
import { gridCustomHtmlRenderer } from '../../../grid/utils/renderers';
import { LicensingConstants } from '../licensingTab/LicensingConstants';
import { SeatType } from '../../../license/types/SeatType';
import { CompanyType } from '../../types/company-type';
import { isLv2License } from '../../../license/common/licenseHelper';
import moment from 'moment-timezone';
import { translateFn } from '../../../utils/translator.helper';
import { LicenseConstants } from '../../../license/LicenseConstants';
import { Bundle } from '../../../license/types/Bundles';
import { LicenseProductIdEnum } from '@nirvana/shared/license-helper';

export class LicenseRenderers {
  private static readonly trialLicensesWithoutOtherSlots: number[] = [LicenseProductIdEnum.ProductIdCws];

  public static getTranslatedProductStatus(productStatus: LicenseProductStatus): string {
    switch (productStatus) {
      case LicenseProductStatus.NO_PRODUCTS:
        return '-';
      case LicenseProductStatus.ACTIVE:
        return translateFn('Active');
      case LicenseProductStatus.TRIAL:
        return translateFn('Trial');
      case LicenseProductStatus.EXPIRED:
        return translateFn('Expired');
      case LicenseProductStatus.INVALID:
        return translateFn('Invalid');
    }
  }

  public static getTranslatedProductName(
    isMonthlyLicense: boolean,
    isInheriting: boolean,
    hasBasicMonthly: boolean,
    productId?: LicenseProductIdEnum
  ): string {
    const translator = BaseWebComponentTranslator.getInstance();

    if (isMonthlyLicense) {
      return this.getMonthlyProductName(isInheriting, hasBasicMonthly);
    }

    if (productId === null || productId === undefined) {
      return translateFn('N/A');
    }

    return translator.translateProductId(productId);
  }

  public static getMonthlyProductName(isInheriting: boolean, hasBasicMonthly: boolean) {
    if (hasBasicMonthly) {
      return translateFn('Basic Monthly Subscription');
    }

    if (isInheriting) {
      return translateFn('Monthly Subscription');
    }

    // this doesn't have to be translated
    return 'Cloud Security for MSP';
  }

  public static getTranslatedProductType(
    isMonthlyLicense: boolean,
    isAddon: boolean,
    productType?: MonthlyProductType
  ): string | DocumentFragment {
    const translator = BaseWebComponentTranslator.getInstance();

    if (isMonthlyLicense) {
      if (productType === undefined || productType === null) {
        return gridCustomHtmlRenderer(`<span class='cell-label'>-</span>`);
      }
      return translator.getMonthlyProductType(productType);
    } else {
      return translator.getYearlyProductType(isAddon);
    }
  }

  public static renderProtectionModel(protectionModel: number) {
    return this.getTranslatedBundleName(protectionModel);
  }

  public static renderProductStatus(productStatus: LicenseProductStatus | undefined | null): string | DocumentFragment {
    if (productStatus === undefined || productStatus === null) {
      return gridCustomHtmlRenderer(`<span class='cell-label'>-</span>`);
    }

    const label = this.getTranslatedProductStatus(productStatus);
    const bulletClass = LicenseProductStatus.getBulletStatusClass(productStatus);

    return gridCustomHtmlRenderer(
      `<span class='cell-label has-bullet ${bulletClass}' id='bullet-cell'>${label}</span>`
    );
  }

  public static renderExpiryDate(expiryDate: number | undefined | null, timezone: string): string {
    let formattedExpiryDate: string;

    if (expiryDate === undefined || expiryDate === null) {
      formattedExpiryDate = '-';
    } else {
      formattedExpiryDate = moment(expiryDate).tz(timezone).format('D MMM YYYY');
    }

    return formattedExpiryDate;
  }

  public static renderTotalSeats(license: LicenseUsage): string {
    const totalSeats = license.totalSeats;
    let totalSeatsText: string;

    if (totalSeats === undefined || totalSeats === null) {
      return '-';
    }

    if (license.isMonthly) {
      totalSeatsText = totalSeats.toString();
    } else {
      const licenseKey = license.licenseKey;
      if (this.isAddonWithoutUsage(license.isAddon ?? false, license.productId)) {
        totalSeatsText = '-';
      } else if (
        license.isTrial &&
        licenseKey &&
        isLv2License(licenseKey) &&
        !this.isAddonWithUsage(license.productId)
      ) {
        totalSeatsText = translateFn('Unlimited');
      } else {
        totalSeatsText = totalSeats.toString();
      }
    }

    return totalSeatsText;
  }

  public static renderUsageBreakdown(
    license: LicenseUsage,
    includesMonthlyUsageBreakdown: boolean = false,
    companyType?: CompanyType
  ): string {
    const translator = BaseWebComponentTranslator.getInstance();

    if (includesMonthlyUsageBreakdown && license.isMonthly) {
      return this.renderUsageBreakdownForMonthly(license, companyType);
    }

    return this.renderUsageBreakdownForYearly(license, translator);
  }

  public static renderUsageBreakdownForMonthly(license: LicenseUsage, companyType?: CompanyType): string {
    if (companyType === undefined) {
      return '-';
    }

    const reservedSeats = Math.max(0, license.reservedSeats ?? 0);
    const unreservedSeats = Math.max(0, license.unreservedSeats ?? 0);
    const totalSeats = Math.max(0, license.totalSeats ?? 0);
    const usedReservedSeats = Math.max(0, license.usedReservedSeats ?? 0);
    const usedUnreservedSeats = Math.max(0, license.usedUnreservedSeats ?? 0);
    const availableReservedSeats = Math.max(0, license.availableReservedSeats ?? 0);
    const availableUnreservedSeats = Math.max(0, license.availableUnreservedSeats ?? 0);

    let usageBreakDownHtml;

    switch (companyType) {
      case CompanyType.Customer:
        if (totalSeats > 0) {
          usageBreakDownHtml =
            translateFn('{usedReservedSeats} used reserved seats, {availableReservedSeats} available reserved seats', {
              usedReservedSeats: usedReservedSeats,
              availableReservedSeats: availableReservedSeats,
            });
        } else {
          usageBreakDownHtml = translateFn('{usedUnreservedSeats} used unreserved seats', {
            usedUnreservedSeats: usedUnreservedSeats,
          });
        }

        break;
      case CompanyType.Partner:
        if (totalSeats > 0) {
          usageBreakDownHtml =
            translateFn('{reservedSeats} reserved seats: {usedReservedSeats} used reserved seats, {availableReservedSeats} available reserved seats', {
              reservedSeats: reservedSeats,
              usedReservedSeats: usedReservedSeats,
              availableReservedSeats: availableReservedSeats,
            }) +
            '<br> ' +
            translateFn('{unreservedSeats} unreserved seats: {usedUnreservedSeats} used unreserved seats, {availableUnreservedSeats} available unreserved seats', {
              unreservedSeats: unreservedSeats,
              usedUnreservedSeats: usedUnreservedSeats,
              availableUnreservedSeats: availableUnreservedSeats
            });
        } else {
          usageBreakDownHtml = translateFn('{usedUnreservedSeats} used unreserved seats', {
            usedUnreservedSeats: usedUnreservedSeats,
          });
        }
        break;
    }

    return usageBreakDownHtml;
  }

  public static renderUsageBreakdownForYearly(license: LicenseUsage, translator: BaseWebComponentTranslator): string {
    if (
      this.isAddonWithoutUsage(license.isAddon ?? false, license.productId) ||
      this.isLicenseWithoutUsage(license.productId)
    ) {
      return '-';
    }

    const usageBreakdown = license.usageBreakdown;

    if (usageBreakdown === undefined || usageBreakdown === null) {
      return '-';
    }

    const totalEntities = Math.max(0, usageBreakdown.entities?.total ?? 0);
    const usedEntities = Math.max(0, usageBreakdown.entities?.used ?? 0);
    const usedServers = Math.max(0, usageBreakdown.servers?.used ?? 0);
    const usedMailboxes = Math.max(0, usageBreakdown.mailboxes?.used ?? 0);
    const usedMobiles = Math.max(0, usageBreakdown.mobiles?.used ?? 0);

    let usageBreakDownHtml;

    if (
      license.isTrial &&
      license.licenseKey &&
      !this.isAddonWithUsage(license.productId) &&
      !this.trialLicensesWithoutOtherSlots.includes(license.productId)
    ) {
      if (isLv2License(license.licenseKey)) {
        usageBreakDownHtml = translateFn(
          '{usedEntities} workstations, {usedServers} servers, {usedMailboxes} mailboxes, {usedMobiles} mobiles',
          {
            usedEntities: Math.max(0, usedEntities - usedServers),
            usedServers: usedServers,
            usedMailboxes: usedMailboxes,
            usedMobiles: usedMobiles,
          }
        );
      } else {
        // SV4 trials have usage, in contrast to LV2 ones.
        usageBreakDownHtml = this.getUsageBreakdownHtml(usageBreakdown, true, true, !license.isInProductTrial);
      }
    } else if (LicensingConstants.BundleLicenseWithoutExchangeMailboxes.includes(license.productId)) {
      usageBreakDownHtml = this.getUsageBreakdownHtml(usageBreakdown, true, false, false);
    } else if (LicensingConstants.BundleLicensesWithoutServerLimit.includes(license.productId)) {
      usageBreakDownHtml = this.getUsageBreakdownHtml(usageBreakdown, false, true, false);
    } else if (LicensingConstants.BundleLicenses.includes(license.productId)) {
      usageBreakDownHtml = this.getUsageBreakdownHtml(usageBreakdown, true, true, false);
    } else {
      const productUnit = translator.getLicenseProductUnit(license.productId);
      usageBreakDownHtml = `${usedEntities}/${totalEntities} ` + productUnit;
    }

    return usageBreakDownHtml;
  }

  private static getUsageBreakdownHtml(
    usageBreakdown,
    hasServers: boolean,
    hasMailboxes: boolean,
    hasMobiles: boolean
  ): string {
    let usageString;
    const totalEntities = Math.max(0, usageBreakdown.entities?.total ?? 0);
    const usedEntities = Math.max(0, usageBreakdown.entities?.used ?? 0);

    if (hasServers) {
      const totalServers = Math.max(0, usageBreakdown.servers?.total ?? 0);
      const usedServers = Math.max(0, usageBreakdown.servers?.used ?? 0);

      usageString =
        translateFn('endpoints: {usedEntities} used, {availableEntities} available', {
          usedEntities: Math.max(0, usedEntities - usedServers),
          availableEntities: Math.max(0, totalEntities - usedEntities),
        }) +
        '<br> ' +
        translateFn('servers: {usedServers} used, {availableServers} available', {
          usedServers: usedServers,
          availableServers: totalServers,
        });
    } else {
      usageString = translateFn('endpoints: {usedEntities} used, {availableEntities} available', {
        usedEntities: usedEntities,
        availableEntities: Math.max(0, totalEntities - usedEntities),
      });
    }

    if (hasMailboxes) {
      const totalMailboxes = Math.max(0, usageBreakdown.mailboxes?.total ?? 0);
      const usedMailboxes = Math.max(0, usageBreakdown.mailboxes?.used ?? 0);

      usageString +=
        '<br> ' +
        translateFn('mailboxes: {usedMailboxes} used, {availableMailboxes} available', {
          usedMailboxes: usedMailboxes,
          availableMailboxes: Math.max(0, totalMailboxes - usedMailboxes),
        });
    }

    if (hasMobiles) {
      const totalMobiles = Math.max(0, usageBreakdown.mobiles?.total ?? 0);
      const usedMobiles = Math.max(0, usageBreakdown.mobiles?.used ?? 0);

      usageString +=
        '<br> ' +
        translateFn('mobile devices: {usedMobiles} used, {availableMobiles} available', {
          usedMobiles: usedMobiles,
          availableMobiles: Math.max(0, totalMobiles - usedMobiles),
        });
    }

    return usageString;
  }

  public static renderAutoRenewal(license: LicenseUsage): DocumentFragment {
    return gridCustomHtmlRenderer(`<span class='cell-label'>${LicenseRenderers.getAutoRenewal(license)}</span>`);
  }

  public static getAutoRenewal(license: LicenseUsage): string {
    const autoRenewal = license.autoRenewal;

    if (autoRenewal === undefined || autoRenewal === null) {
      return translateFn('No');
    }

    return translateFn('Yes ({months} months)', { months: autoRenewal });
  }

  public static renderSubscriptionEndDate(license: LicenseUsage, timezone: string): DocumentFragment {
    return gridCustomHtmlRenderer(
      `<span class='cell-label'>${LicenseRenderers.getSubscriptionEndDate(license, timezone)}</span>`
    );
  }

  public static getSubscriptionEndDate(license: LicenseUsage, timezone: string): string {
    const subscriptionEndDate = license.subscriptionEndDate;
    const hasInheritedSubscriptionEndDate = license.hasInheritedSubscriptionEndDate;

    if (subscriptionEndDate === undefined || subscriptionEndDate === null) {
      return translateFn('Never');
    }
    let formattedExpiryDate = moment(subscriptionEndDate).tz(timezone).format('D MMM YYYY');

    if (hasInheritedSubscriptionEndDate) {
      formattedExpiryDate = formattedExpiryDate + ' - ' + translateFn('Inherited');
    }

    return formattedExpiryDate;
  }

  public static getNumberOfSeatTypes(licenseList: LicenseUsage[]): number {
    const allSeatTypes = new Set();

    for (const license of licenseList) {
      const seatTypes = this.getLicenseSeatTypes(license);

      for (const type of seatTypes) {
        allSeatTypes.add(type);
      }
    }

    return allSeatTypes.size;
  }

  private static getLicenseSeatTypes(license: LicenseUsage): SeatType[] {
    const usageBreakdown = license.usageBreakdown;

    if (
      this.isAddonWithoutUsage(license.isAddon ?? false, license.productId) ||
      this.isLicenseWithoutUsage(license.productId) ||
      usageBreakdown === undefined ||
      usageBreakdown === null
    ) {
      return [];
    }

    if (
      license.isTrial &&
      license.licenseKey &&
      isLv2License(license.licenseKey) &&
      !this.isAddonWithUsage(license.productId)
    ) {
      if (!license.isInProductTrial) {
        return [SeatType.Workstation, SeatType.Server, SeatType.Mailbox, SeatType.MobileDevice];
      } else {
        return [SeatType.Workstation, SeatType.Server, SeatType.Mailbox];
      }
    } else if (LicensingConstants.BundleLicenseWithoutExchangeMailboxes.includes(license.productId)) {
      return [SeatType.Endpoint, SeatType.Server];
    } else if (LicensingConstants.BundleLicensesWithoutServerLimit.includes(license.productId)) {
      return [SeatType.Endpoint, SeatType.Mailbox];
    } else if (LicensingConstants.BundleLicenses.includes(license.productId)) {
      return [SeatType.Endpoint, SeatType.Server, SeatType.Mailbox];
    }

    switch (license.productId) {
      case LicenseProductIdEnum.ProductIdCeps:
        return [SeatType.Entity];
      case LicenseProductIdEnum.ProductIdMsp:
        return [SeatType.Seat];
      case LicenseProductIdEnum.ProductIdEpcDesktopsExtended:
        return [SeatType.PhysicalWorkstation];
      case LicenseProductIdEnum.ProductIdEpcServersExtended:
        return [SeatType.PhysicalServer];
      case LicenseProductIdEnum.ProductIdGlobal:
        return [SeatType.Entity];
      case LicenseProductIdEnum.ProductIdSveVdiExtended:
        return [SeatType.VirtualDesktop];
      case LicenseProductIdEnum.ProductIdSveVsExtended:
        return [SeatType.VirtualServer];
      case LicenseProductIdEnum.ProductIdContainerProtection:
        return [SeatType.ContainerHost];
      case LicenseProductIdEnum.ProductIdEmailSecurity:
        return [SeatType.Mailbox];
      case LicenseProductIdEnum.ProductIdMobileSecurity:
        return [SeatType.MobileDevice];
      case LicenseProductIdEnum.ProductIdDesktopSecurity:
        return [SeatType.Workstation];
      case LicenseProductIdEnum.ProductIdServerSecurity:
        return [SeatType.Server];
      case LicenseProductIdEnum.ProductIdXdrProductivitySensors:
        return [SeatType.Mailbox];
      case LicenseProductIdEnum.ProductIdXdrCloudSensors:
        return [SeatType.User];
      case LicenseProductIdEnum.ProductIdXdrIdentitySensors:
        return [SeatType.User];
      case LicenseProductIdEnum.ProductIdXdrNetworkSensors:
        return [SeatType.User];
      case LicenseProductIdEnum.ProductIdXdrSensitiveDataSensors:
        return [SeatType.User];
      default:
        return [SeatType.Endpoint];
    }
  }

  public static isAddonWithoutUsage(isAddon: boolean, productId: LicenseProductIdEnum | undefined): boolean {
    const isAddonWithUsage =
      typeof productId === 'undefined' ? false : LicensingConstants.AddonsWithUsage.includes(productId);

    return isAddon && !isAddonWithUsage;
  }

  public static isAddonWithUsage(productId: LicenseProductIdEnum | undefined): boolean {
    return typeof productId === 'undefined' ? false : LicensingConstants.AddonsWithUsage.includes(productId);
  }

  public static isLicenseWithoutUsage(productId: LicenseProductIdEnum | undefined): boolean {
    if (productId === undefined || productId === null) {
      return false;
    }

    return [
      LicenseProductIdEnum.ProductIdXdrProductivitySensors,
      LicenseProductIdEnum.ProductIdXdrNetworkSensors,
      LicenseProductIdEnum.ProductIdXdrCloudSensors,
      LicenseProductIdEnum.ProductIdXdrIdentitySensors,
      LicenseProductIdEnum.ProductIdRemoteAccess,
      LicenseProductIdEnum.ProductIdXdrSensitiveDataSensors,
      LicenseProductIdEnum.ProductIdComplianceManager,
      LicenseProductIdEnum.ProductIdExposureManagement,
    ].includes(productId);
  }

  public static getTranslatedBundleName(bundleId: number): string {
    switch (bundleId) {
      case Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE:
        return translateFn('A la carte');
      case Bundle.PRODUCT_MSP_BUNDLE_SECURE:
        return translateFn('Secure');
      case Bundle.PRODUCT_MSP_BUNDLE_SECURE_PLUS:
        return translateFn('Secure Plus');
      case Bundle.PRODUCT_MSP_BUNDLE_SECURE_EXTRA:
        return translateFn('Secure Extra');
      default:
        return translateFn('N/A');
    }
  }

  public static getTranslatedBundleDescription(bundleId: number): string {
    switch (bundleId) {
      case Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE:
        return translateFn(
          'Custom tailored protection allowing you to select the features that align with your specific requirements.'
        );
      case Bundle.PRODUCT_MSP_BUNDLE_SECURE:
        return translateFn(
          'Actionable threat intelligence and prevention, and real time threat detection and incident response.'
        );
      case Bundle.PRODUCT_MSP_BUNDLE_SECURE_PLUS:
        return translateFn('Enhances the Secure model, by providing proactive incident response management services.');
      case Bundle.PRODUCT_MSP_BUNDLE_SECURE_EXTRA:
        return translateFn(
          'Build upon the Secure Plus model, enriching detection sources with intelligence across your environment.'
        );
      default:
        return translateFn('N/A');
    }
  }

  public static getBundlesDescription(): string[] {
    const description = translateFn(
      'Protection models provide an additional level of customization to the Endpoint Security product.'
    );
    const linkText = translateFn('Learn more');
    const linkHref = LicenseConstants.InfoLinks.BundlesDetails;
    return [description, linkHref, linkText];
  }

  public static getParentCompanyMdrContactInfoRequired(): string {
    return translateFn(
      `To activate the MDR service the company's direct Partner must have Contact details for MDR information filled in.`
    );
  }
}
